import React, { useState, useMemo, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ColDef } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { Client, Group } from '@/types'
import { ClientFormModal } from './ClientFormModal'
import { LicenseTerminateModal } from './LicenseTerminateModal'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { agGridLocaleFr } from '@/config/ag-grid-locale-fr'
import { useClientsStore } from '@/stores/clients.store'
import { useAttachedClientsStore } from '@/stores/attached-clients.store'
import { useGroupsStore } from '@/stores/groups.store'
import { useAuth } from '@/hooks/useAuth'
import {
  canManageClients,
  canExportCompositionDuParc,
} from '@/utils/role-access'
import { AGTableWrapper } from '@/components/ag-grid/AGTableWrapper'
import { getClientsColumnDefs } from '@/column-defs/clients.column-defs'
import { toastError, toastSuccess } from '@/utils/toast'
import { exportAllClientsToCompositionDuParc } from '@/utils/composition-du-parc-export'
import { filterClientsForUser } from '@/utils/client-scope'
import { Download } from 'phosphor-react'

type ClientsTabMode = 'direct' | 'attached'

interface ClientsTabProps {
  mode?: ClientsTabMode
}

export const ClientsTab: React.FC<ClientsTabProps> = ({ mode = 'direct' }) => {
  const isAttachedMode = mode === 'attached'

  const directClients = useClientsStore(state => state.clients)
  const attachedClients = useAttachedClientsStore(state => state.clients)
  const clients = isAttachedMode ? attachedClients : directClients

  const directDeleteClient = useClientsStore(state => state.deleteClient)
  const directBlockClient = useClientsStore(state => state.blockClient)
  const directUnblockClient = useClientsStore(state => state.unblockClient)
  const directTerminateClient = useClientsStore(state => state.terminateClient)
  const directUpdateClient = useClientsStore(state => state.updateClient)

  const attachedDeleteClient = useAttachedClientsStore(state => state.deleteClient)
  const attachedBlockClient = useAttachedClientsStore(state => state.blockClient)
  const attachedUnblockClient = useAttachedClientsStore(state => state.unblockClient)
  const attachedTerminateClient = useAttachedClientsStore(state => state.terminateClient)
  const attachedUpdateClient = useAttachedClientsStore(state => state.updateClient)

  const deleteClient = isAttachedMode ? attachedDeleteClient : directDeleteClient
  const blockClient = isAttachedMode ? attachedBlockClient : directBlockClient
  const unblockClient = isAttachedMode ? attachedUnblockClient : directUnblockClient
  const terminateClient = isAttachedMode ? attachedTerminateClient : directTerminateClient
  const updateClient = isAttachedMode ? attachedUpdateClient : directUpdateClient

  const groups = useGroupsStore(state => state.groups)
  const { user } = useAuth()
  const canEditClients = canManageClients(user)
  const canExportClients = canExportCompositionDuParc(user)
  const showResellerColumn = isAttachedMode
  const visibleClients = useMemo(
    () => filterClientsForUser(clients, user, mode),
    [clients, user, mode]
  )

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [deleteClientId, setDeleteClientId] = useState<string | null>(null)
  const [terminateClientTarget, setTerminateClientTarget] = useState<Client | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isTerminating, setIsTerminating] = useState(false)

  const handleCreate = () => {
    setSelectedClient(null)
    setIsFormOpen(true)
  }

  const handleEdit = useCallback((client: Client) => {
    setSelectedClient(client)
    setIsFormOpen(true)
  }, [])

  const handleDeleteClick = useCallback((id: string) => {
    setDeleteClientId(id)
  }, [])

  const handleTerminateClick = useCallback((client: Client) => {
    setTerminateClientTarget(client)
  }, [])

  const handleDeleteConfirm = async () => {
    if (!deleteClientId) return

    setIsDeleting(true)
    try {
      await deleteClient(deleteClientId)
      setDeleteClientId(null)
      toastSuccess('Licence archivée avec succès')
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erreur lors de l'archivage de la licence"
      toastError(errorMessage)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteClientId(null)
  }

  const handleBlock = useCallback(
    async (client: Client) => {
      try {
        await blockClient(client._id)
        toastSuccess('Licence bloquée avec succès')
      } catch (error) {
        toastError(
          error instanceof Error ? error.message : 'Erreur lors du blocage de la licence'
        )
      }
    },
    [blockClient]
  )

  const handleUnblock = useCallback(
    async (client: Client) => {
      try {
        await unblockClient(client._id)
        toastSuccess('Licence débloquée avec succès')
      } catch (error) {
        toastError(
          error instanceof Error ? error.message : 'Erreur lors du déblocage de la licence'
        )
      }
    },
    [unblockClient]
  )

  const handleTerminateConfirm = async () => {
    if (!terminateClientTarget) return

    setIsTerminating(true)
    try {
      const updated = await terminateClient(terminateClientTarget._id)
      setTerminateClientTarget(null)
      toastSuccess(
        updated.noticeEndAt
          ? `Licence résiliée. Fin de préavis : ${new Date(updated.noticeEndAt).toLocaleDateString('fr-FR')}`
          : 'Licence résiliée avec succès'
      )
    } catch (error) {
      toastError(
        error instanceof Error ? error.message : 'Erreur lors de la résiliation de la licence'
      )
    } finally {
      setIsTerminating(false)
    }
  }

  const handleExportAll = useCallback(async () => {
    try {
      await exportAllClientsToCompositionDuParc(visibleClients)
      toastSuccess(`Export réussi pour ${visibleClients.length} licence(s)`)
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Erreur lors de l'export du fichier composition du parc"
      toastError(errorMessage)
    }
  }, [visibleClients])

  const handleFormClose = () => {
    setIsFormOpen(false)
    setSelectedClient(null)
  }

  const handleFormSuccess = () => {
    handleFormClose()
  }

  const getGroupName = useMemo(
    () =>
      (group: string | Group): string => {
        if (typeof group === 'string') {
          const found = groups?.find(g => g._id === group)
          return found?.name || group
        }
        return group.name
      },
    [groups]
  )

  const columnDefs: ColDef[] = useMemo(
    () =>
      getClientsColumnDefs({
        getGroupName,
        handleEdit,
        handleDeleteClick,
        handleBlock,
        handleUnblock,
        handleTerminateClick,
        canEdit: canEditClients,
        showResellerColumn,
      }),
    [
      getGroupName,
      handleEdit,
      handleDeleteClick,
      handleBlock,
      handleUnblock,
      handleTerminateClick,
      canEditClients,
      showResellerColumn,
    ]
  )

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 100,
    }),
    []
  )

  const pageTitle = isAttachedMode ? 'Liste des clients rattachés' : 'Liste des clients'
  const gridCollection = isAttachedMode ? 'attached-clients' : 'clients'

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">{pageTitle}</h2>
        <div className="flex items-center gap-2">
          {canExportClients && (
            <Button
              onClick={handleExportAll}
              variant="outline"
              className="flex items-center gap-2"
              disabled={visibleClients.length === 0}
            >
              <Download className="w-4 h-4" weight="regular" />
              Exporter composition du parc
            </Button>
          )}
          {canEditClients && !isAttachedMode && (
            <Button onClick={handleCreate}>Nouveau client</Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm overflow-hidden">
        <div
          className="ag-theme-alpine"
          style={{ height: '600px', width: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <AGTableWrapper collection={gridCollection}>
            <AgGridReact
              theme="legacy"
              rowData={visibleClients}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={20}
              paginationPageSizeSelector={[10, 20, 50, 100]}
              animateRows={true}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              suppressCellFocus={false}
              headerHeight={48}
              rowHeight={48}
              localeText={agGridLocaleFr}
            />
          </AGTableWrapper>
        </div>
      </div>

      {isFormOpen && (
        <ClientFormModal
          client={selectedClient}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
          onUpdateClient={isAttachedMode ? updateClient : undefined}
        />
      )}

      {deleteClientId && (
        <Modal
          title="Archiver la licence"
          isDanger={true}
          confirmButtonText="Archiver"
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          isLoading={isDeleting}
          size="sm"
        >
          <p className="text-[var(--color-text-primary)]">
            Êtes-vous sûr de vouloir archiver cette licence ? Elle ne sera plus visible dans la
            liste active.
          </p>
        </Modal>
      )}

      {terminateClientTarget && (
        <LicenseTerminateModal
          client={terminateClientTarget}
          isLoading={isTerminating}
          onCancel={() => setTerminateClientTarget(null)}
          onConfirm={handleTerminateConfirm}
        />
      )}
    </div>
  )
}
